/**
 * Affiliates → Leaderboard: who brought paying customers, over the last 30
 * or 90 days or all time. A customer counts once, for the affiliate whose
 * code they signed up with (api lib/command/affiliates.ts leaderboard()).
 * Every number with people behind it opens them.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Trophy, UserPlus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button, Skeleton, useToast } from "@/components/ui";
import { Card, Empty, Pill, Segmented, Stat, num, pct, timeAgo, usd } from "@/features/command/ui";
import { KIND_META, kindLabel } from "./shared";
import { PeopleDrawer, type PeopleDrawerState } from "./PeopleDrawer";
import type { Leaderboard, LeaderRow, ReferredPerson } from "./types";

type Window = "30" | "90" | "all";
type Metric = "signups" | "trials" | "paying" | "revenue";

const WINDOW_LABEL: Record<Window, string> = { "30": "in the last 30 days", "90": "in the last 90 days", all: "so far" };
const METRIC_TITLE: Record<Metric, string> = { signups: "Signed up", trials: "Started a trial", paying: "Started paying", revenue: "Paid us" };

function inWindow(iso: string | null, days: number | null): boolean {
  if (!iso) return false;
  return days == null || Date.parse(iso) >= Date.now() - days * 86400_000;
}

function peopleFor(people: ReferredPerson[], metric: Metric, days: number | null): ReferredPerson[] {
  return people.filter((p) =>
    metric === "signups" ? inWindow(p.signed_up_at, days)
      : metric === "trials" ? inWindow(p.trial_at, days)
        : metric === "paying" ? inWindow(p.paid_at, days)
          : p.revenue > 0);
}

export function LeaderboardTab({ onTab }: { onTab: (tab: "recruit" | "assets") => void }) {
  const toast = useToast();
  const { pathname, search } = useLocation();
  const [win, setWin] = useState<Window>("30");
  const [data, setData] = useState<Leaderboard | null>(null);
  const [drawer, setDrawer] = useState<PeopleDrawerState | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null);
    api.get<Leaderboard>("/affiliates/leaderboard", { days: win })
      .then((d) => { if (live) setData(d); })
      .catch((err) => { if (live) { toast.error(err instanceof ApiError ? err.message : "Couldn't load the leaderboard"); setData({ days: null, rows: [], totals: { signups: 0, trials: 0, paying: 0, revenue: 0, earned: 0, due: 0, conversion: null } }); } });
    return () => { live = false; };
  }, [win, toast]);

  const days = win === "all" ? null : Number(win);
  const ranked = useMemo(() => (data?.rows ?? []).filter((r) => r.signups || r.trials || r.paying || r.revenue), [data]);
  const rest = useMemo(() => (data?.rows ?? []).filter((r) => !(r.signups || r.trials || r.paying || r.revenue)), [data]);

  const open = (metric: Metric, rows: LeaderRow[], who?: string) => {
    const people = rows.flatMap((r) => peopleFor(r.people, metric, days).map((p) => ({ p, via: r.name })));
    setDrawer({
      title: `${METRIC_TITLE[metric]}${who ? ` through ${who}` : " through affiliates"}`,
      hint: WINDOW_LABEL[win].replace(/^./, (c) => c.toUpperCase()),
      people: people.map(({ p, via }) => ({
        account_id: p.account_id,
        contact_id: p.contact_id,
        name: p.name,
        sub: `${who ? "" : `via ${via} · `}signed up ${timeAgo(p.signed_up_at)}${p.plan_code ? ` · ${p.plan_code}` : ""}`,
        detail: metric === "revenue" ? usd(p.revenue, { cents: p.revenue % 1 !== 0 }) : metric === "paying" ? "Paying" : metric === "trials" ? (p.paid_at ? "Now paying" : "Trial") : p.paid_at ? "Paying" : null,
      })),
    });
  };

  const t = data?.totals;
  const cell = (r: LeaderRow, metric: Metric, value: number, text: string) =>
    value > 0 ? (
      <button type="button" onClick={() => open(metric, [r], r.name)} className="rounded-md px-1.5 py-0.5 font-medium tabular-nums text-zinc-100 hover:bg-indigo-500/15 hover:text-indigo-200">
        {text}
      </button>
    ) : <span className="px-1.5 tabular-nums text-zinc-600">{text}</span>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-zinc-400">Ranked by customers who started paying, then by money collected.</p>
        <Segmented<Window> size="sm" value={win} onChange={setWin} options={[{ value: "30", label: "30 days" }, { value: "90", label: "90 days" }, { value: "all", label: "All time" }]} />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {(["signups", "trials", "paying", "revenue"] as Metric[]).map((m) => {
          const value = t ? (m === "revenue" ? usd(t.revenue) : num(t[m])) : <Skeleton className="h-7 w-12" />;
          const n = t ? t[m] : 0;
          const label = m === "signups" ? "Signups" : m === "trials" ? "Trials" : m === "paying" ? "Started paying" : "Collected";
          const sub = m === "paying" && t?.conversion != null ? `${pct(t.conversion)} of their signups pay` : m === "revenue" ? "Payments from referred customers" : WINDOW_LABEL[win];
          return n > 0 ? (
            <button key={m} type="button" className="text-left" onClick={() => open(m, ranked)}>
              <Stat label={label} value={value} sub={sub} />
            </button>
          ) : <Stat key={m} label={label} value={value} sub={sub} />;
        })}
      </div>

      <Card as="section" className="min-w-0 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-zinc-800/70 px-4 py-3">
          <Trophy className="h-4 w-4 text-indigo-300" />
          <h2 className="text-sm font-semibold text-zinc-100">Leaderboard</h2>
          <span className="ml-auto text-[11px] text-zinc-500">Earned and owed are all time</span>
        </div>
        {data == null ? (
          <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-11 w-full rounded-lg" />)}</div>
        ) : ranked.length === 0 ? (
          <Empty
            icon={Trophy}
            title={`Nobody referred anyone ${WINDOW_LABEL[win]}`}
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button size="sm" leftIcon={<UserPlus className="h-3.5 w-3.5" />} onClick={() => onTab("recruit")}>See who to invite</Button>
                <Button size="sm" variant="secondary" onClick={() => onTab("assets")}>Add something to share</Button>
              </div>
            }
          >
            Affiliates share more when they have fresh posts to copy and see others winning. Invite your most active customers, and give them something to post.
          </Empty>
        ) : (
          <>
            {/* Wide screens: a table. */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="border-b border-zinc-800/70 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    <th className="w-10 py-2.5 pl-4 pr-1 font-semibold">#</th>
                    <th className="px-2 py-2.5 font-semibold">Affiliate</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Signups</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Trials</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Paying</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Collected</th>
                    <th className="px-2 py-2.5 text-right font-semibold" title="Of their signups in this window, how many pay">Pay rate</th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-right font-semibold">Earned / owed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {ranked.map((r, i) => (
                    <tr key={r.id} className="transition-colors hover:bg-zinc-800/25">
                      <td className="py-2.5 pl-4 pr-1 text-[13px] font-semibold tabular-nums text-zinc-500">{i + 1}</td>
                      <td className="max-w-[260px] px-2 py-2.5">
                        <Link to={`/affiliates/${r.id}`} state={{ from: pathname + search }} className="block truncate font-medium text-zinc-100 hover:text-indigo-300">{r.name}</Link>
                        {r.kind !== "casual" && <Pill color={KIND_META[r.kind]?.color} className="mt-0.5">{kindLabel(r.kind)}</Pill>}
                      </td>
                      <td className="px-2 py-2.5 text-right">{cell(r, "signups", r.signups, num(r.signups))}</td>
                      <td className="px-2 py-2.5 text-right">{cell(r, "trials", r.trials, num(r.trials))}</td>
                      <td className="px-2 py-2.5 text-right">{cell(r, "paying", r.paying, num(r.paying))}</td>
                      <td className="px-2 py-2.5 text-right">{cell(r, "revenue", r.revenue, usd(r.revenue))}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-zinc-400">{r.conversion == null ? "–" : pct(r.conversion)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                        <span className="text-zinc-300">{usd(r.earned)}</span>
                        <span className="text-zinc-600"> / </span>
                        <span className={r.due > 0 ? "font-semibold text-amber-300" : "text-zinc-500"}>{usd(r.due)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phone: one card each. */}
            <div className="divide-y divide-zinc-800/60 md:hidden">
              {ranked.map((r, i) => (
                <div key={r.id} className="space-y-2.5 px-4 py-3.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13px] font-semibold tabular-nums text-zinc-500">{i + 1}</span>
                    <Link to={`/affiliates/${r.id}`} state={{ from: pathname + search }} className="min-w-0 truncate font-medium text-zinc-100">{r.name}</Link>
                    <span className="ml-auto shrink-0 text-xs tabular-nums text-zinc-400">
                      earned {usd(r.earned)}{r.due > 0 && <span className="text-amber-300"> · owed {usd(r.due)}</span>}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    {([["signups", r.signups, num(r.signups), "Signups"], ["trials", r.trials, num(r.trials), "Trials"], ["paying", r.paying, num(r.paying), "Paying"], ["revenue", r.revenue, usd(r.revenue), "Collected"]] as [Metric, number, string, string][]).map(([m, v, text, label]) => (
                      <button
                        key={m}
                        type="button"
                        disabled={!v}
                        onClick={() => open(m, [r], r.name)}
                        className="rounded-lg bg-zinc-900/50 px-1 py-1.5 enabled:hover:bg-indigo-500/10"
                      >
                        <div className={cn("text-sm font-semibold tabular-nums", v ? "text-zinc-100" : "text-zinc-600")}>{text}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {data && rest.length > 0 && (
          <div className="border-t border-zinc-800/60 px-4 py-2.5">
            <button type="button" onClick={() => setShowAll((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-300 hover:text-indigo-200">
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showAll && "rotate-180")} />
              {showAll ? "Hide" : "Show"} {rest.length} {rest.length === 1 ? "affiliate" : "affiliates"} with nothing {WINDOW_LABEL[win]}
            </button>
            {showAll && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {rest.map((r) => (
                  <Link key={r.id} to={`/affiliates/${r.id}`} state={{ from: pathname + search }} className="rounded-full bg-zinc-800/70 px-2.5 py-1 text-xs text-zinc-300 hover:text-indigo-200">
                    {r.name}{r.earned > 0 ? ` · earned ${usd(r.earned)}` : ""}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
      <p className="text-[11px] text-zinc-500">
        A customer counts once, for the affiliate whose code they signed up with. Collected is what referred customers actually paid {WINDOW_LABEL[win]}.
      </p>

      <PeopleDrawer state={drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}
